import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { SecureInput } from "@/components/forms";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@repo/ui";
import {
  Users as UsersIcon,
  Loader2,
  Plus,
  MoreVertical,
  ShieldCheck,
  Shield,
  KeyRound,
  UserX,
  Check,
  X,
  ShieldAlert,
  PenLine,
} from "lucide-react";
import {
  ROLES,
  ROLE_DISPLAY_NAMES,
  getRolePermissionSummary,
  NON_OVERRIDABLE_ROLES,
  type Permission,
  type Role,
  type AccessTag,
} from "@/lib/permissions";
import { PermissionGate } from "@/components/security";
import { usePermission } from "@/hooks/use-permission";
import { UserPermissionsDialog } from "@/components/users/user-permissions-dialog";
import { usePharmacyStore } from "@/lib/stores/pharmacy-store";
import {
  createUser,
  updateUserRole,
  updateInitials,
  resetPassword,
  toggleUserStatus,
} from "@/lib/api/users";
import { showErrorToast } from "@/lib/utils/error-display";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { useApproval } from "@/context/approval-context";
import { ApiClientError } from "@/lib/api/client";

export const Route = createFileRoute("/_app/users")({
  component: UsersPage,
});

const ACCESS_TAG_CONFIG: Record<
  AccessTag,
  { icon: typeof Check; className: string }
> = {
  full: { icon: Check, className: "text-emerald-600" },
  limited: { icon: ShieldAlert, className: "text-amber-600" },
  approval: { icon: ShieldAlert, className: "text-amber-600" },
  view: { icon: Check, className: "text-blue-600" },
  none: { icon: X, className: "text-red-500" },
};

const ACCESS_LABELS: Record<AccessTag, string> = {
  full: "Full",
  limited: "Limited",
  approval: "Approval",
  view: "View only",
  none: "None",
};

const ACCESS_LABEL_STYLES: Record<AccessTag, string> = {
  full: "border-emerald-200 text-emerald-700 bg-emerald-50",
  limited: "border-amber-200 text-amber-700 bg-amber-50",
  approval: "border-amber-200 text-amber-700 bg-amber-50",
  view: "border-blue-200 text-blue-700 bg-blue-50",
  none: "border-red-200 text-red-600 bg-red-50",
};

function UsersPage() {
  const users = usePharmacyStore((s) => s.users);
  const isLoading = !usePharmacyStore((s) => s.isUsersLoaded);
  const { toast } = useToast();
  const { requestApproval, isApprovalPending } = useApproval();
  const { isAllowed: canManageUsers } = usePermission(
    "users:deactivate" as Permission
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    initials: "",
    role: "" as string,
    password: "",
    confirmPassword: "",
    mustChangePassword: false,
  });
  const [permissionMode, setPermissionMode] = useState<
    "defaults" | "customize"
  >("defaults");

  // Change role dialog state
  const [changeRoleUser, setChangeRoleUser] = useState<{
    id: string;
    name: string;
    role: string;
  } | null>(null);
  const [newRole, setNewRole] = useState("");

  // Reset password dialog state
  const [resetPasswordUser, setResetPasswordUser] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Change initials dialog state
  const [changeInitialsUser, setChangeInitialsUser] = useState<{
    id: string;
    name: string;
    initials: string;
  } | null>(null);
  const [newInitials, setNewInitials] = useState("");

  // Permissions dialog state
  const [permissionsUser, setPermissionsUser] = useState<{
    id: string;
    name: string;
    role: string;
  } | null>(null);

  const handleChangeRole = async (
    e?: React.FormEvent,
    approvalToken?: string
  ) => {
    e?.preventDefault();
    if (!changeRoleUser) return;
    setIsSubmitting(true);
    try {
      await updateUserRole({
        userId: changeRoleUser.id,
        role: newRole,
        approvalToken,
      });
      toast({ title: "Role updated successfully", variant: "success" });
      setChangeRoleUser(null);
      usePharmacyStore.getState().invalidateUsers();
      usePharmacyStore.getState().fetchUsers();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => handleChangeRole(undefined, token),
          actionDescription: "Update user role",
        });
        return;
      }
      showErrorToast(error, "Update role");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (
    e?: React.FormEvent,
    approvalToken?: string
  ) => {
    e?.preventDefault();
    if (!resetPasswordUser) return;
    setIsSubmitting(true);
    try {
      await resetPassword({
        userId: resetPasswordUser.id,
        newPassword,
        approvalToken,
      });
      toast({ title: "Password reset successfully", variant: "success" });
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => handleResetPassword(undefined, token),
          actionDescription: "Reset user password",
        });
        return;
      }
      showErrorToast(error, "Reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeInitials = async (
    e?: React.FormEvent,
    approvalToken?: string
  ) => {
    e?.preventDefault();
    if (!changeInitialsUser) return;
    setIsSubmitting(true);
    try {
      await updateInitials({
        userId: changeInitialsUser.id,
        initials: newInitials,
        approvalToken,
      });
      toast({ title: "Initials updated successfully", variant: "success" });
      setChangeInitialsUser(null);
      setNewInitials("");
      usePharmacyStore.getState().invalidateUsers();
      usePharmacyStore.getState().fetchUsers();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => handleChangeInitials(undefined, token),
          actionDescription: "Change user initials",
        });
        return;
      }
      showErrorToast(error, "Change initials");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (userId: string, approvalToken?: string) => {
    try {
      await toggleUserStatus({ userId, isActive: false, approvalToken });
      toast({ title: "User status updated", variant: "success" });
      usePharmacyStore.getState().invalidateUsers();
      usePharmacyStore.getState().fetchUsers();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => handleToggleStatus(userId, token),
          actionDescription: "Toggle user status",
        });
        return;
      }
      showErrorToast(error, "Toggle user status");
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "default" as const;
      case "supervising_pharmacist":
      case "pharmacist":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  const handleSubmit = async (e?: React.FormEvent, approvalToken?: string) => {
    e?.preventDefault();
    if (!form.mustChangePassword && form.password !== form.confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords match.",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await createUser({
        name: `${form.firstName} ${form.lastName}`.trim(),
        initials: form.initials.toUpperCase(),
        role: form.role,
        password: form.password,
        mustChangePassword: form.mustChangePassword,
        approvalToken,
      });
      toast({ title: "User created successfully", variant: "success" });
      setDialogOpen(false);

      // Capture values before reset
      const createdRole = form.role;
      const createdName = `${form.firstName} ${form.lastName}`.trim();
      const shouldCustomize =
        permissionMode === "customize" &&
        !NON_OVERRIDABLE_ROLES.has(createdRole as Role) &&
        canManageUsers;

      setForm({
        firstName: "",
        lastName: "",
        initials: "",
        role: "",
        password: "",
        confirmPassword: "",
        mustChangePassword: false,
      });
      setPermissionMode("defaults");

      // Force re-fetch users to update the list
      usePharmacyStore.getState().invalidateUsers();
      await usePharmacyStore.getState().fetchUsers();

      if (shouldCustomize) {
        // Prefer the response ID for lookup; fall back to name
        const createdId = response?.user?.id;
        const updatedUsers = usePharmacyStore.getState().users;
        const createdUser = createdId
          ? updatedUsers.find((u) => u.id === createdId)
          : undefined;

        setPermissionsUser({
          id: createdUser?.id ?? createdId ?? "",
          name: createdUser?.full_name || createdUser?.initials || createdName,
          role: createdUser?.role || createdRole,
        });
      }
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => handleSubmit(undefined, token),
          actionDescription: "Create user",
        });
        return;
      }
      showErrorToast(error, "Create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleSummary = form.role
    ? getRolePermissionSummary(form.role as Role)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">Users</h1>
        </div>
        <PermissionGate permission="users:create">
          <Button
            data-testid="user-add-button"
            size="sm"
            className="gap-1.5 h-9"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </PermissionGate>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Staff Members</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No users found
            </div>
          ) : (
            <Table data-testid="user-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Initials</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  {canManageUsers && <TableHead className="w-[50px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {user.initials}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {user.full_name || user.initials}
                    </TableCell>
                    <TableCell>
                      <Badge
                        data-testid={`user-role-badge-${user.id}`}
                        variant={getRoleBadgeVariant(user.role)}
                      >
                        {ROLE_DISPLAY_NAMES[user.role as Role] || user.role}
                      </Badge>
                    </TableCell>
                    {canManageUsers && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              data-testid={`user-row-actions-trigger-${user.id}`}
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 data-[state=open]:bg-muted"
                            >
                              <MoreVertical className="h-4 w-4" />
                              <span className="sr-only">More actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-[200px]"
                          >
                            <PermissionGate permission="users:deactivate">
                              <DropdownMenuItem
                                onClick={() => {
                                  setPermissionsUser({
                                    id: user.id,
                                    name: user.full_name || user.initials,
                                    role: user.role,
                                  });
                                }}
                              >
                                <Shield className="mr-2 h-4 w-4" />
                                Manage Permissions
                              </DropdownMenuItem>
                            </PermissionGate>
                            <PermissionGate permission="users:deactivate">
                              <DropdownMenuItem
                                data-testid={`user-change-role-${user.id}`}
                                onClick={() => {
                                  setChangeRoleUser({
                                    id: user.id,
                                    name: user.full_name || user.initials,
                                    role: user.role,
                                  });
                                  setNewRole(user.role);
                                }}
                              >
                                <ShieldCheck className="mr-2 h-4 w-4" />
                                Change Role
                              </DropdownMenuItem>
                            </PermissionGate>
                            <PermissionGate permission="users:deactivate">
                              <DropdownMenuItem
                                onClick={() => {
                                  setChangeInitialsUser({
                                    id: user.id,
                                    name: user.full_name || user.initials,
                                    initials: user.initials,
                                  });
                                  setNewInitials(user.initials);
                                }}
                              >
                                <PenLine className="mr-2 h-4 w-4" />
                                Change Initials
                              </DropdownMenuItem>
                            </PermissionGate>
                            <PermissionGate permission="users:deactivate">
                              <DropdownMenuItem
                                onClick={() => {
                                  setResetPasswordUser({
                                    id: user.id,
                                    name: user.full_name || user.initials,
                                  });
                                  setNewPassword("");
                                }}
                              >
                                <KeyRound className="mr-2 h-4 w-4" />
                                Reset Password
                              </DropdownMenuItem>
                            </PermissionGate>
                            <PermissionGate permission="users:deactivate">
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`user-deactivate-${user.id}`}
                                onClick={() => handleToggleStatus(user.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Deactivate
                              </DropdownMenuItem>
                            </PermissionGate>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog
        open={dialogOpen && !isApprovalPending}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setPermissionMode("defaults");
          }
        }}
      >
        <DialogContent
          className={`max-h-[85vh] overflow-y-auto ${
            canManageUsers &&
            form.role &&
            !NON_OVERRIDABLE_ROLES.has(form.role as Role)
              ? "sm:max-w-[720px]"
              : "sm:max-w-[420px]"
          }`}
        >
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new staff member account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div
              className={`flex gap-6 ${
                canManageUsers &&
                form.role &&
                !NON_OVERRIDABLE_ROLES.has(form.role as Role)
                  ? "flex-row"
                  : "flex-col"
              }`}
            >
              {/* Left side - Form fields */}
              <div className="flex-1 space-y-4 min-w-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      data-testid="user-form-first-name"
                      required
                      autoComplete="off"
                      value={form.firstName}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, firstName: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      data-testid="user-form-last-name"
                      required
                      autoComplete="off"
                      value={form.lastName}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, lastName: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="initials">Initials</Label>
                    <Input
                      id="initials"
                      data-testid="user-form-initials"
                      required
                      autoComplete="off"
                      maxLength={5}
                      value={form.initials}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          initials: e.target.value
                            .replace(/[^A-Za-z]/g, "")
                            .toUpperCase(),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={form.role}
                      onValueChange={(value) =>
                        setForm((f) => ({ ...f, role: value }))
                      }
                    >
                      <SelectTrigger data-testid="user-form-role-trigger">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {ROLE_DISPLAY_NAMES[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <SecureInput
                    id="password"
                    data-testid="user-form-password"
                    value={form.password}
                    onChange={(val) =>
                      setForm((f) => ({ ...f, password: val }))
                    }
                  />
                </div>
                {!form.mustChangePassword && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <SecureInput
                      id="confirmPassword"
                      value={form.confirmPassword}
                      onChange={(val) =>
                        setForm((f) => ({
                          ...f,
                          confirmPassword: val,
                        }))
                      }
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="mustChangePassword"
                    checked={form.mustChangePassword}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({
                        ...f,
                        mustChangePassword: checked === true,
                      }))
                    }
                  />
                  <Label
                    htmlFor="mustChangePassword"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Require password change on first login
                  </Label>
                </div>
              </div>

              {/* Right side - Permissions */}
              {canManageUsers &&
                form.role &&
                !NON_OVERRIDABLE_ROLES.has(form.role as Role) && (
                  <div className="w-[260px] flex-shrink-0 space-y-3 rounded-lg border bg-muted/30 p-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      Permissions
                    </Label>
                    <RadioGroup
                      value={permissionMode}
                      onValueChange={(v) =>
                        setPermissionMode(v as "defaults" | "customize")
                      }
                      className="space-y-2"
                    >
                      <label
                        htmlFor="perm-defaults"
                        className="flex items-start gap-2.5 rounded-md border bg-background p-2.5 cursor-pointer hover:bg-muted/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                      >
                        <RadioGroupItem
                          value="defaults"
                          id="perm-defaults"
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium">
                            Use role defaults
                          </span>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Standard {ROLE_DISPLAY_NAMES[form.role as Role]}{" "}
                            permissions.
                          </p>
                        </div>
                      </label>
                      <label
                        htmlFor="perm-customize"
                        className="flex items-start gap-2.5 rounded-md border bg-background p-2.5 cursor-pointer hover:bg-muted/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                      >
                        <RadioGroupItem
                          value="customize"
                          id="perm-customize"
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium">Customize</span>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Edit permissions after creating.
                          </p>
                        </div>
                      </label>
                    </RadioGroup>

                    {/* Role summary */}
                    {roleSummary && (
                      <div className="space-y-1.5 pt-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          {ROLE_DISPLAY_NAMES[form.role as Role]} default
                          access:
                        </span>
                        <div className="grid grid-cols-1 gap-1">
                          {roleSummary.map((item) => {
                            const config = ACCESS_TAG_CONFIG[item.access];
                            const Icon = config.icon;
                            return (
                              <div
                                key={item.label}
                                className="flex items-center justify-between gap-2 text-xs"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Icon
                                    className={`h-3 w-3 shrink-0 ${config.className}`}
                                  />
                                  <span className="text-muted-foreground truncate">
                                    {item.label}
                                  </span>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1.5 py-0 h-4 shrink-0 font-medium ${ACCESS_LABEL_STYLES[item.access]}`}
                                >
                                  {ACCESS_LABELS[item.access]}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="user-form-submit"
                type="submit"
                disabled={isSubmitting || !form.role}
              >
                {isSubmitting ? "Creating..." : "Create User"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog
        open={!!changeRoleUser && !isApprovalPending}
        onOpenChange={(open) => !open && setChangeRoleUser(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update the role for {changeRoleUser?.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeRole} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newRole">Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="user-change-role-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {ROLE_DISPLAY_NAMES[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setChangeRoleUser(null)}
              >
                Cancel
              </Button>
              <Button
                data-testid="user-change-role-submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={!!resetPasswordUser && !isApprovalPending}
        onOpenChange={(open) => {
          if (!open) {
            setResetPasswordUser(null);
            setNewPassword("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordUser?.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                required
                minLength={4}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setResetPasswordUser(null);
                  setNewPassword("");
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Initials Dialog */}
      <Dialog
        open={!!changeInitialsUser && !isApprovalPending}
        onOpenChange={(open) => {
          if (!open) {
            setChangeInitialsUser(null);
            setNewInitials("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Change Initials</DialogTitle>
            <DialogDescription>
              Update initials for {changeInitialsUser?.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeInitials} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newInitials">New Initials</Label>
              <Input
                id="newInitials"
                required
                maxLength={5}
                value={newInitials}
                onChange={(e) =>
                  setNewInitials(
                    e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase()
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                2-5 uppercase letters
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setChangeInitialsUser(null);
                  setNewInitials("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  newInitials.length < 2 ||
                  newInitials === changeInitialsUser?.initials
                }
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Permissions Dialog */}
      <UserPermissionsDialog
        user={permissionsUser}
        open={!!permissionsUser}
        onOpenChange={(open) => {
          if (!open) setPermissionsUser(null);
        }}
      />
    </div>
  );
}
